import {
  adminLeadStageHistoryPageSizes,
  crmRecordStatuses,
  leadPipelineStages,
  leadSourceKinds,
  type AdminLeadDetail,
  type AdminLeadListItem,
  type AdminLeadStageHistoryItem,
  type AdminLeadStageHistoryPage,
  type LeadPipelineStage,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { canAccessAdminCrm } from "./admin-crm-authorization";
import { isAdminLeadDuplicateSummaryConsistent } from "./admin-lead-duplicate-model";
import { isAdminLeadGuestConversionStateConsistent } from "./admin-lead-guest-conversion-model";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";
import { isBoundedOpaqueCrmCursor } from "./opaque-cursor-model";
import { isAdminLeadAssignmentSnapshotConsistent } from "./lead-assignment-model";

export const ADMIN_LEAD_TIMELINE_COVERAGE =
  "FOUNDATION_AND_RECORDED_PIPELINE_HISTORY" as const;

export const adminLeadTimelineKinds = [
  "LEAD_CREATED",
  "FIRST_TOUCH_CAPTURED",
  "LEAD_ARCHIVED",
  "PIPELINE_STAGE_CHANGED",
] as const;

export type AdminLeadTimelineKind = (typeof adminLeadTimelineKinds)[number];

export type AdminLeadTimelineItem =
  | {
      readonly kind: Exclude<AdminLeadTimelineKind, "PIPELINE_STAGE_CHANGED">;
      readonly occurredAt: string;
    }
  | {
      readonly kind: "PIPELINE_STAGE_CHANGED";
      readonly occurredAt: string;
      readonly fromStage: LeadPipelineStage;
      readonly toStage: LeadPipelineStage;
      readonly pipelineRevision: number;
    };

export function canViewAdminLeadDetail(
  subject: AuthorizationSubject | null,
): boolean {
  return canAccessAdminCrm(subject);
}

export function isCanonicalCrmTimestamp(value: string | null): value is string {
  if (
    value === null ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }

  const timestamp = new Date(value);
  return (
    Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value
  );
}

export function buildAdminLeadTimeline(
  detail: AdminLeadDetail,
  stageHistory: readonly AdminLeadStageHistoryItem[] = [],
): readonly AdminLeadTimelineItem[] {
  const timeline: AdminLeadTimelineItem[] = [];

  if (isCanonicalCrmTimestamp(detail.summary.createdAt)) {
    timeline.push({
      kind: "LEAD_CREATED",
      occurredAt: detail.summary.createdAt,
    });
  }

  if (
    detail.summary.sourceKind !== null &&
    isCanonicalCrmTimestamp(detail.sourceCapturedAt)
  ) {
    timeline.push({
      kind: "FIRST_TOUCH_CAPTURED",
      occurredAt: detail.sourceCapturedAt,
    });
  }

  for (const transition of stageHistory) {
    if (isAdminLeadStageHistoryItemConsistent(transition, detail)) {
      timeline.push({
        kind: "PIPELINE_STAGE_CHANGED",
        occurredAt: transition.occurredAt,
        fromStage: transition.fromStage,
        toStage: transition.toStage,
        pipelineRevision: transition.pipelineRevision,
      });
    }
  }

  if (
    detail.summary.leadRecordStatus === "ARCHIVED" &&
    isCanonicalCrmTimestamp(detail.leadArchivedAt)
  ) {
    timeline.push({
      kind: "LEAD_ARCHIVED",
      occurredAt: detail.leadArchivedAt,
    });
  }

  return timeline.sort(
    (left, right) =>
      Date.parse(right.occurredAt) - Date.parse(left.occurredAt) ||
      left.kind.localeCompare(right.kind, "en"),
  );
}

function isKnownPipelineStage(value: string): value is LeadPipelineStage {
  return (leadPipelineStages as readonly string[]).includes(value);
}

export function isAdminLeadStageHistoryItemConsistent(
  value: unknown,
  detail: AdminLeadDetail,
): value is AdminLeadStageHistoryItem {
  if (typeof value !== "object" || value === null) return false;
  const item = value as AdminLeadStageHistoryItem;
  return (
    isKnownPipelineStage(item.fromStage) &&
    isKnownPipelineStage(item.toStage) &&
    item.fromStage !== item.toStage &&
    Number.isSafeInteger(item.pipelineRevision) &&
    item.pipelineRevision > 0 &&
    isCanonicalCrmTimestamp(item.occurredAt) &&
    item.occurredAt >= detail.summary.createdAt &&
    item.occurredAt <= detail.summary.updatedAt
  );
}

export function isAdminLeadStageHistoryConsistent(
  items: readonly AdminLeadStageHistoryItem[],
  detail: AdminLeadDetail,
  requireCurrentStageMatch = true,
): boolean {
  const revisions = new Set<number>();

  if (
    requireCurrentStageMatch &&
    items.length > 0 &&
    items[0]!.toStage !== detail.summary.pipelineStage
  ) {
    return false;
  }

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]!;
    if (
      !isAdminLeadStageHistoryItemConsistent(item, detail) ||
      revisions.has(item.pipelineRevision)
    ) {
      return false;
    }
    revisions.add(item.pipelineRevision);

    const newer = items[index - 1];
    if (
      newer &&
      (newer.pipelineRevision !== item.pipelineRevision + 1 ||
        newer.occurredAt < item.occurredAt ||
        item.toStage !== newer.fromStage)
    ) {
      return false;
    }
  }

  return true;
}

export function isAdminLeadStageHistoryPageConsistent(
  value: unknown,
  detail: AdminLeadDetail,
): value is AdminLeadStageHistoryPage {
  if (typeof value !== "object" || value === null) return false;
  const page = value as AdminLeadStageHistoryPage;
  return (
    Array.isArray(page.items) &&
    page.items.length <= adminLeadStageHistoryPageSizes[0] &&
    !(page.items.length === 0 && page.nextCursor !== null) &&
    (page.nextCursor === null || isBoundedOpaqueCrmCursor(page.nextCursor)) &&
    isAdminLeadStageHistoryConsistent(page.items, detail, false)
  );
}

export function mergeAdminLeadStageHistory(
  current: readonly AdminLeadStageHistoryItem[],
  incoming: readonly AdminLeadStageHistoryItem[],
): readonly AdminLeadStageHistoryItem[] | null {
  const byRevision = new Map(
    current.map((item) => [item.pipelineRevision, item] as const),
  );

  for (const item of incoming) {
    if (byRevision.has(item.pipelineRevision)) return null;
    byRevision.set(item.pipelineRevision, item);
  }

  return [...byRevision.values()].sort(
    (left, right) =>
      right.pipelineRevision - left.pipelineRevision ||
      Date.parse(right.occurredAt) - Date.parse(left.occurredAt),
  );
}

const crmRecordStatusSet: ReadonlySet<string> = new Set(crmRecordStatuses);
const leadSourceKindSet: ReadonlySet<string> = new Set(leadSourceKinds);

function isCanonicalDisplayName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 200 &&
    value === value.trim() &&
    !/[\u0000\n\r]/.test(value)
  );
}

function isCanonicalEmail(value: unknown): value is string | null {
  if (value === null) return true;
  if (
    typeof value !== "string" ||
    value.length < 3 ||
    value.length > 320 ||
    value !== value.toLowerCase() ||
    value !== value.trim() ||
    /[ \t\n\r]/.test(value)
  ) {
    return false;
  }
  const at = value.indexOf("@");
  return at > 0 && at === value.lastIndexOf("@") && at < value.length - 1;
}

function isCanonicalPhone(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === "string" && /^\+[1-9]\d{2,14}$/.test(value))
  );
}

function isCanonicalSourceToken(
  value: unknown,
  maximumLength: number,
): value is string | null {
  return (
    value === null ||
    (typeof value === "string" &&
      value.length >= 1 &&
      value.length <= maximumLength &&
      value === value.trim() &&
      value === value.toLowerCase() &&
      /^[a-z0-9._~-]+$/.test(value))
  );
}

export function isAdminLeadSummaryConsistent(
  value: unknown,
): value is AdminLeadListItem {
  if (typeof value !== "object" || value === null) return false;
  const summary = value as AdminLeadListItem;
  return (
    typeof summary.leadId === "string" &&
    isSafeLeadIdPathSegment(summary.leadId) &&
    isCanonicalDisplayName(summary.displayName) &&
    isCanonicalEmail(summary.emailNormalized) &&
    isCanonicalPhone(summary.phoneE164) &&
    (summary.emailNormalized !== null || summary.phoneE164 !== null) &&
    isKnownPipelineStage(summary.pipelineStage) &&
    typeof summary.leadRecordStatus === "string" &&
    crmRecordStatusSet.has(summary.leadRecordStatus) &&
    typeof summary.contactRecordStatus === "string" &&
    crmRecordStatusSet.has(summary.contactRecordStatus) &&
    (summary.sourceKind === null ||
      (typeof summary.sourceKind === "string" &&
        leadSourceKindSet.has(summary.sourceKind))) &&
    isCanonicalSourceToken(summary.sourceKey, 64) &&
    isCanonicalSourceToken(summary.campaignKey, 128) &&
    (summary.sourceKind !== null ||
      (summary.sourceKey === null && summary.campaignKey === null)) &&
    isAdminLeadAssignmentSnapshotConsistent(summary) &&
    isAdminLeadDuplicateSummaryConsistent(summary.duplicateSummary) &&
    (summary.duplicateSummary.eligibility === "ELIGIBLE") ===
      (summary.leadRecordStatus === "ACTIVE" &&
        summary.contactRecordStatus === "ACTIVE") &&
    isCanonicalCrmTimestamp(summary.createdAt) &&
    isCanonicalCrmTimestamp(summary.updatedAt) &&
    summary.updatedAt >= summary.createdAt
  );
}

function leadLifecycleIsConsistent(detail: AdminLeadDetail): boolean {
  const { summary } = detail;
  if (
    !isCanonicalCrmTimestamp(summary.createdAt) ||
    !isCanonicalCrmTimestamp(summary.updatedAt) ||
    summary.updatedAt < summary.createdAt
  ) {
    return false;
  }

  if (summary.leadRecordStatus === "ACTIVE") {
    return detail.leadArchivedAt === null;
  }

  return (
    isCanonicalCrmTimestamp(detail.leadArchivedAt) &&
    detail.leadArchivedAt >= summary.createdAt &&
    detail.leadArchivedAt <= summary.updatedAt
  );
}

export function isAdminLeadDetailConsistent(
  value: unknown,
): value is AdminLeadDetail {
  if (typeof value !== "object" || value === null) return false;
  const detail = value as AdminLeadDetail;
  if (!isAdminLeadSummaryConsistent(detail.summary)) return false;

  const { summary } = detail;
  const sourceIsConsistent =
    summary.sourceKind === null
      ? summary.sourceKey === null &&
        summary.campaignKey === null &&
        detail.sourceCapturedAt === null
      : isCanonicalCrmTimestamp(detail.sourceCapturedAt);

  return (
    sourceIsConsistent &&
    leadLifecycleIsConsistent(detail) &&
    isAdminLeadGuestConversionStateConsistent(detail)
  );
}
